/**
 * Resolve public Storybook links for the environment serving the current app.
 *
 * Local development and first-party Moonfall hosts share in place. Third-party
 * embeds keep using the temporary apex fallback until their launch handoff is
 * explicitly configured.
 */
(function createStorybookShareUrl(root, factory) {
    const api = factory(root);
    if (root) root.MoonfallStorybookUrl = api;
}(typeof window !== 'undefined' ? window : null, function buildApi(root) {
    'use strict';

    const DEFAULT_EXTERNAL_SHARE_BASE_URL = 'https://moonfall.io';
    const DEFAULT_LANGUAGE = 'en';
    const SUPPORTED_LANGUAGES = new Set(['en', 'ja', 'ko', 'zh-CN', 'de', 'fr', 'es', 'pt']);
    const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
    const CHANNELS = Object.freeze({
        native: { source: 'native_share', medium: 'referral' },
        twitter: { source: 'twitter', medium: 'social' },
        facebook: { source: 'facebook', medium: 'social' },
        reddit: { source: 'reddit', medium: 'social' },
        whatsapp: { source: 'whatsapp', medium: 'social' },
        telegram: { source: 'telegram', medium: 'social' },
        bluesky: { source: 'bluesky', medium: 'social' },
        threads: { source: 'threads', medium: 'social' },
        discord: { source: 'discord', medium: 'social' },
        copy: { source: 'copy_link', medium: 'referral' },
        download: { source: 'save_image', medium: 'referral' },
    });

    function normalizeExplicitBaseUrl(value) {
        if (typeof value !== 'string' || !value.trim()) return null;

        let parsed;
        try {
            parsed = new URL(value.trim());
        } catch (_) {
            return null;
        }

        if (
            !['http:', 'https:'].includes(parsed.protocol)
            || parsed.username
            || parsed.password
            || parsed.search
            || parsed.hash
        ) {
            return null;
        }

        const path = parsed.pathname.replace(/\/+$/, '');
        return path ? `${parsed.origin}${path}` : parsed.origin;
    }

    function parseLocation(locationLike) {
        if (!locationLike) return null;
        const candidate = typeof locationLike.href === 'string'
            ? locationLike.href
            : locationLike.origin;
        if (typeof candidate !== 'string' || !candidate) return null;

        try {
            return new URL(candidate);
        } catch (_) {
            return null;
        }
    }

    function isCurrentProductHost(hostname) {
        const host = String(hostname || '').toLowerCase();
        return host === 'localhost'
            || host.endsWith('.localhost')
            || host === '127.0.0.1'
            || host === '[::1]'
            || host === 'moonfall.io'
            || host.endsWith('.moonfall.io');
    }

    function resolveShareBaseUrl(options = {}) {
        const override = options.override !== undefined
            ? options.override
            : root?.MOONFALL_SHARE_BASE_URL;
        const explicitBaseUrl = normalizeExplicitBaseUrl(override);
        if (explicitBaseUrl) return explicitBaseUrl;

        const currentLocation = parseLocation(options.location || root?.location);
        if (
            currentLocation
            && ['http:', 'https:'].includes(currentLocation.protocol)
            && isCurrentProductHost(currentLocation.hostname)
        ) {
            return currentLocation.origin;
        }

        return DEFAULT_EXTERNAL_SHARE_BASE_URL;
    }

    function normalizeLanguage(value) {
        const normalized = String(value || '').trim().replace(/_/g, '-');
        if (!normalized) return null;
        const lower = normalized.toLowerCase();
        if (['zh', 'zh-cn', 'zh-hans', 'zh-sg'].includes(lower)) return 'zh-CN';
        const base = lower.split('-')[0];
        return SUPPORTED_LANGUAGES.has(base) ? base : null;
    }

    function readStoredLanguage() {
        try {
            return root?.localStorage?.getItem('moonfall_language');
        } catch (_) {
            return null;
        }
    }

    function resolveLanguage(options = {}) {
        const currentLocation = parseLocation(options.location || root?.location);
        const requested = [
            options.language,
            currentLocation?.searchParams.get('lang'),
            typeof root?.i18n?.getLanguage === 'function' ? root.i18n.getLanguage() : null,
            readStoredLanguage(),
        ];
        for (const candidate of requested) {
            const language = normalizeLanguage(candidate);
            if (language) return language;
        }
        return DEFAULT_LANGUAGE;
    }

    function buildStorybookUrl(shortCode, options = {}) {
        if (typeof shortCode !== 'string' || !shortCode) return null;
        const url = new URL(
            `${resolveShareBaseUrl(options)}/s/${encodeURIComponent(shortCode)}`,
        );
        url.searchParams.set('lang', resolveLanguage(options));
        const version = normalizeContentVersion(options.version);
        if (version !== null) url.searchParams.set('v', String(version));
        return url.href;
    }

    function normalizeContentVersion(value) {
        return typeof value === 'number'
            && Number.isSafeInteger(value)
            && value >= 0
            ? value
            : null;
    }

    function buildStorybookImageUrl(shortCode, options = {}) {
        if (typeof shortCode !== 'string' || !shortCode) return null;
        const url = new URL(
            `${resolveShareBaseUrl(options)}/api/storybook/${encodeURIComponent(shortCode)}/share-image`,
        );
        const version = normalizeContentVersion(options.version);
        if (version !== null) url.searchParams.set('v', String(version));
        return url.href;
    }

    function buildPlayUrl(options = {}) {
        const url = new URL(`${resolveShareBaseUrl(options)}/play`);
        const currentLocation = parseLocation(options.location || root?.location);
        url.searchParams.set('lang', resolveLanguage(options));
        if (options.propagateAttribution && currentLocation) {
            for (const key of ATTRIBUTION_KEYS) {
                const value = currentLocation.searchParams.get(key);
                if (value && value.length <= 100) url.searchParams.set(key, value);
            }
        }
        return url.href;
    }

    function withShareAttribution(
        rawUrl,
        channel,
        options: {
            mediaKind?: string,
            shareFormat?: string,
            campaign?: string,
            content?: string,
        } = {},
    ) {
        const policy = CHANNELS[channel];
        if (!policy || typeof rawUrl !== 'string') return rawUrl;

        let url;
        try {
            url = new URL(rawUrl);
        } catch (_) {
            return rawUrl;
        }
        if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
            return rawUrl;
        }

        const mediaKind = ['manga', 'cover', 'story'].includes(options.mediaKind)
            ? options.mediaKind
            : 'story';
        const payloadKind = options.shareFormat === 'image_attachment'
            ? 'image'
            : 'link';
        /* THE CAMPAIGN IS NOT ALWAYS THE STORYBOOK'S. This bar is shared -- a
           Moon Exchange listing shares through exactly the same controls -- and
           these tags were hardcoded, so every listing the owner sent carried
           `utm_campaign=storybook_share&utm_content=story_link`. The analytics
           events already solved this with `ctx.analyticsPrefix`; the URL simply
           never got the same treatment. A caller that passes nothing keeps the
           original values, so the storybook's own links are unchanged. */
        const safe = (value: unknown) => (
            typeof value === 'string' && /^[a-z0-9_]{1,40}$/.test(value) ? value : ''
        );
        const campaign = safe(options.campaign) || 'storybook_share';
        const content = safe(options.content) || `${mediaKind}_${payloadKind}`;
        url.searchParams.set('utm_source', policy.source);
        url.searchParams.set('utm_medium', policy.medium);
        url.searchParams.set('utm_campaign', campaign);
        url.searchParams.set('utm_content', content);
        return url.href;
    }

    return Object.freeze({
        DEFAULT_EXTERNAL_SHARE_BASE_URL,
        ATTRIBUTION_KEYS,
        buildPlayUrl,
        buildStorybookImageUrl,
        buildStorybookUrl,
        resolveLanguage,
        resolveShareBaseUrl,
        withShareAttribution,
    });
}));
